import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ConnectionPageShared } from './ConnectionPageShared';
import { useSession } from '../../contexts/SessionContext';
import { joinChatRoom, sendMessage } from '../../services/chat/chatService';
import { useNavigation } from '../../contexts/NavigationContext';
import { firstNameOf } from '../../utils/contactUtils';
// Note: All storage operations now go through ConnectionStateContext
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import {
  getProfileData,
  saveGreetingText,
  getSentSharedFields,
  setLastReadTimestamp,
} from '../../services/storage/userDataStorage';
import { setMeetingVenue } from '../../services/meetings/meetingRows';
import { readStagedSheets, shareSheetShownKey } from '../../services/exchange/connectSheets';
import {
  buildContactLinkMessage,
  buildImageChatMessage,
  buildSentChatMessage,
  buildShareLineMessage,
  buildSystemMeetingMessage,
  buildContactShareMessage,
  buildMetadataUpdatedMessage,
} from '../../services/chat/messageBuilders';
import { CONTACT_FIELDS, getBaseType, SHEET_SHARE_ORDER } from '../../utils/contactKeys';
import { uploadChatImage } from '../../services/storage/chatImageUpload';
import ShareSheet from '../UISystem/ShareSheet';
import HostThreadVenueSheet from './variants/thread/HostThreadVenueSheet';
import { isThreadVariant } from '../../utils/exchangeVariant';
import ShareTray from '../UISystem/ShareTray';
import HostPhotoChip from '../UI/Cards/HostPhotoChip';
import { commitSharedFields } from '../../services/sharing/commitSharedFields';
import {
  canQuickLastShare,
  formatLastShareLabel,
  getAvailableSharingFields,
  isFullProfileRedundant,
  readLastSharedFields,
  readGlobalNameSharing,
  fullProfileFields,
} from '../../utils/sharingFields';
import { getProfileUserId, getSenderId } from '../../utils/identityUtils';
import { CHAT_TOKENS } from '../../styles/UISystem';
import useHeartbeat from '../../hooks/useHeartbeat';
import usePeerStatus from '../../hooks/usePeerStatus';
import useIsRoyaleAdmin from '../../hooks/useIsRoyaleAdmin';
import { hasSentInvite, sendRoyaleInvite } from '../../services/chat/royaleInvite';
import { formatSystemMessageLocation, formatMetSentence } from '../../utils/locationFormatters';
import SharingSelectionOverlay from '../Overlay/SharingSelectionOverlay';

import LocationOverlay from '../Overlay/LocationOverlay';
import { saveCustomVenue } from '../../services/location/providers/radarProvider';
import { getInactiveStatus, getBannerDisplayName } from '../../utils/anonInactiveStatus';
import { getConnectionStatus } from '../../services/activeSession';
import { persistMessage } from '../../services/chat/messageStore';
import {
  logFunnelInviteSent,
  logConnectionLocation,
  logMomentEntryTapped,
  logMomentSent,
} from '../../services/analytics/eventLogger';
import { sendPhotoMoment } from '../../services/moments/momentStore';
import { isNewExperience } from '../../utils/newExperience';
import { mark, flush } from '../../services/connectionTiming';
import { getConnectionDisplayName } from '../../utils/connectionName';
import {
  isPushSupported,
  getPermissionState,
  registerAndSubscribe,
} from '../../services/pushNotificationService';

/**
 * ProfileConnectionPage - PROFILE USER VIEW ONLY
 *
 * This component is specifically for PROFILE USERS viewing connection data.
 *
 * IMPORTANT: This component is separate from AnonConnectionPage and has different
 * behavior. Be careful not to modify this component when intending to change
 * AnonConnectionPage behavior.
 *
 * ✅ MIGRATION COMPLETE: Fully migrated to context-only state management
 * - No local state for data (anonData, connectionMetadata, note)
 * - All data read directly from ConnectionStateContext
 * - Message handlers update context only (no local state updates)
 * - React automatically re-renders when context changes
 * - Single source of truth: ConnectionStateContext
 */

const ProfileConnectionPage = () => {
  // Use session context as the single source of truth for chatRoomId
  const { sessionChatRoomId } = useSession();

  // Use sessionChatRoomId directly - no fallback needed
  const chatRoomId = sessionChatRoomId;

  // MIGRATION COMPLETE: No local state for data - read directly from context
  // Context is the single source of truth for all connection data

  const [isLoading, setIsLoading] = useState(false); // Always start with false to prevent loading indicator

  // Debug state for tracking connection
  const [channelState, setChannelState] = useState('initializing');

  // Location overlay state (opens when system message tapped)
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);

  // Full own-profile object — the share sheet's gates and one-tap sends need
  // more than the name (phone, currentCity, available-contact count)
  const [ownProfileData, setOwnProfileData] = useState(null);

  // Connection confirmation status for deleted banner logic
  const [connectionStatus, setConnectionStatus] = useState(null);

  // DB heartbeat presence — true when peer's heartbeat is fresh
  const peerOnline = useHeartbeat(chatRoomId, getProfileUserId());
  // activityStatus + peerLocation come from usePeerStatus below (needs anonData first)

  // Derive connection status for banner logic (deleted/confirmed/offline)
  const dotStatus = useMemo(() => {
    if (!connectionStatus) return null;
    if (connectionStatus === 'deleted') return 'deleted';
    return 'offline'; // no longer drives a dot — just banner logic
  }, [connectionStatus]);

  // Share more overlay state (triggered by the share sheet's +; the composer Plus opens the tray)
  const [showShareMoreOverlay, setShowShareMoreOverlay] = useState(false);

  // Composer share tray (decision #14) — single-action value rows
  const [showShareTray, setShowShareTray] = useState(false);

  // Load profile user's own data on mount
  useEffect(() => {
    const loadProfileInfo = async () => {
      const profileData = await getProfileData();
      setOwnProfileData(profileData || null);
    };
    loadProfileInfo();
  }, [chatRoomId]);

  // Connection timing: profile is now on the chat page — flush timing data
  useEffect(() => {
    mark('chat_page_mounted');
    flush();
  }, [chatRoomId]);

  // Poll connection status for status dot (guest may not have confirmed yet)
  useEffect(() => {
    if (!chatRoomId) return;
    let attempts = 0;
    const maxAttempts = 4;

    const checkStatus = async () => {
      const status = await getConnectionStatus(chatRoomId);
      setConnectionStatus(status);
      if (status === 'confirmed' || status === 'deleted') {
        clearInterval(pollRef);
      }
      attempts++;
      if (attempts >= maxAttempts) clearInterval(pollRef);
    };

    checkStatus();
    const pollRef = setInterval(checkStatus, 3000);
    return () => clearInterval(pollRef);
  }, [chatRoomId]);

  // Re-check status when peer comes online — catches confirmations that happened
  // after the initial poll window (e.g., missed connection recovery)
  useEffect(() => {
    if (
      peerOnline &&
      connectionStatus &&
      connectionStatus !== 'confirmed' &&
      connectionStatus !== 'deleted'
    ) {
      getConnectionStatus(chatRoomId).then(setConnectionStatus);
    }
  }, [peerOnline, connectionStatus, chatRoomId]);

  // Get navigation context
  const { navigateTo, goBack } = useNavigation();

  // Push: refresh the subscription when permission is already granted. The
  // pre-prompt itself lives on the People list (first-visit moment) — the old
  // source==='connection-list' trigger here was dead: no nav path to this page
  // ever sent that source after the detail-page redesign.
  useEffect(() => {
    if (!isPushSupported()) return;
    if (getPermissionState() === 'granted') {
      registerAndSubscribe();
    }
  }, []);

  // Get connection data and context methods for message persistence
  const connectionData = useConnectionData(chatRoomId);
  const {
    updateChatMessages,
    addChatMessage,
    updateNotes,
    updateConnectionMetadata,
    updateContactSharingStatus,
  } = useConnectionContext();

  // MIGRATION COMPLETE: Read data directly from context - no sync needed
  // anonData contains contact info from Anon user or PXP guest
  const anonData = connectionData?.anonData || {};
  const connectionMetadata = useMemo(
    () => connectionData?.connectionMetadata || {},
    [connectionData]
  );
  const note = connectionData?.notes || '';

  // Peer activity + live location for header subtitle (heartbeat drives "Active now")
  const { activityStatus, peerLocation } = usePeerStatus(
    anonData?.senderProfileUserId,
    chatRoomId,
    { peerOnline }
  );

  // Invite to Royale — visibility (must be after anonData declaration)
  // Derive inviteSent from chat history so it persists across navigation
  const chatMessages = connectionData?.chatMessages || [];
  const inviteSent = hasSentInvite(chatMessages);
  // Only an admin ACCOUNT may invite — the server's answer, never the display
  // name (signup-invite-simple; the server also refuses anyone else's invite)
  const viewerIsAdmin = useIsRoyaleAdmin();
  const isPXP = connectionMetadata?.exchangeType === 'pxp';

  // Anon inactivity banner — pure timestamp math, no DB query
  const inactiveStatus = getInactiveStatus(connectionData || {});
  // Dead-room rule (user call, 2026-07-27): the full-inactive tier — the same
  // one that disables the chat input — also quiets the conversation
  // affordances (camera, +, talk prompt). The Invite button deliberately
  // survives (the door back in); soft tier keeps everything.
  const inactiveRoom = inactiveStatus === 'inactive';
  let inactiveBanner = null;
  if (inactiveStatus === 'soft_inactive' || inactiveStatus === 'inactive') {
    const displayName = getBannerDisplayName(connectionData || {});
    inactiveBanner =
      inactiveStatus === 'inactive'
        ? `${displayName} is no longer active`
        : `${displayName} may no longer be active`;
  }

  // Compute final header activity status — merges heartbeat, last_active_at, and anon inactivity
  const headerActivityStatus = useMemo(() => {
    // Heartbeat or last_active_at activity takes priority
    if (activityStatus) return activityStatus;
    // Anon connections: show inactivity labels for 24h+
    if (inactiveStatus === 'soft_inactive') return { text: 'Probably inactive', isActive: false };
    if (inactiveStatus === 'inactive') return { text: 'Inactive', isActive: false };
    return null;
  }, [activityStatus, inactiveStatus]);

  const showInviteButton =
    !isPXP &&
    viewerIsAdmin &&
    !inviteSent &&
    !!anonData?.name &&
    (anonData?.sharedFields?.length || 0) > 0;

  // NOTE: DB sync is now handled by ConnectionStateContext.loadConnectionData()
  // which reads from localStorage cache first (instant render), then syncs from DB in background.
  // This eliminates the flicker that occurred when this component did its own async DB fetch.

  // Mark chat as read when entering this page AND when new messages arrive
  // This ensures messages seen in real-time don't show as unread on the list
  useEffect(() => {
    if (chatRoomId) {
      setLastReadTimestamp(chatRoomId);
    }
  }, [chatRoomId, connectionData?.chatMessages]);

  // Exit loading state once we have context data
  useEffect(() => {
    if (connectionData) {
      setIsLoading(false);
    }
  }, [connectionData]);

  // Sharing gate useEffect removed — Profile lands directly in chat
  // Exchange paths stage the connect sheets (connectSheets; QRScreen reads isRoomStaged)
  // venue_pending_${chatRoomId} was dead code (written but never read) — removed

  // Add system message when connection is established (connectionMetadata is available)
  // System message shows location and date/time
  // Use connectionData references directly to avoid exhaustive-deps warnings
  const contextConnectionMetadata = connectionData?.connectionMetadata;
  const contextChatMessages = connectionData?.chatMessages;

  // Recovery only: create a system message if none exists.
  // Venue edits update the system message directly in the LocationOverlay handler —
  // NOT here. This prevents metadata_updated WebSocket from the other user
  // triggering system message rewrites on this side.
  useEffect(() => {
    if (!contextConnectionMetadata) return;
    // Time-first (Anon↔Profile): only a meetingDate is required; the venue is added
    // when it resolves. GPS/Radar can fail, so the banner must not hinge on a venue.
    if (!contextConnectionMetadata.meetingDate) return;
    if (!updateChatMessages) return;

    const chatMessages = contextChatMessages || [];
    const userId = getProfileUserId();

    const hasOwnMessage = chatMessages.some(
      (msg) => msg.type === 'system' && msg.id?.endsWith(`_${userId}`)
    );
    const hasOldFormatMessage = chatMessages.some(
      (msg) => msg.type === 'system' && msg.id === `system_${chatRoomId}`
    );

    if (hasOwnMessage || hasOldFormatMessage) return;

    const isPXPConnection = contextConnectionMetadata?.exchangeType === 'pxp';
    let systemMessage;

    if (isPXPConnection) {
      const locationContent = formatSystemMessageLocation(contextConnectionMetadata);
      if (!locationContent) return;
      const compact =
        (contextConnectionMetadata.meetingDate || '').replace(/\D/g, '').slice(0, 14) || 'unknown';
      systemMessage = buildSystemMeetingMessage({
        id: `system_${chatRoomId}_${compact}_${userId}`,
        content: locationContent,
        meetingDate: contextConnectionMetadata.meetingDate,
        meetingUtcOffset: contextConnectionMetadata.meetingUtcOffset,
      });
    } else {
      // Time-first: venue when available, else '' (banner shows the time only). The
      // meeting time renders via formatTimePillShort, aging day → date → year.
      const content = formatSystemMessageLocation(contextConnectionMetadata);
      systemMessage = buildSystemMeetingMessage({
        id: `system_${chatRoomId}`,
        content,
        meetingDate: contextConnectionMetadata.meetingDate,
        meetingUtcOffset: contextConnectionMetadata.meetingUtcOffset,
      });
    }
    updateChatMessages(chatRoomId, [systemMessage, ...chatMessages]);
    persistMessage(chatRoomId, systemMessage);
  }, [contextConnectionMetadata, contextChatMessages, chatRoomId, updateChatMessages]);

  // Pass-through to updateChatMessages — stable reference, never recreates on message changes.
  // updateChatMessages accepts updater functions (prev => next) so it always reads latest state.
  const handleSetChatMessages = useCallback(
    (updater) => updateChatMessages(chatRoomId, updater),
    [chatRoomId, updateChatMessages]
  );

  // Join chat room — channel subscription keeps the global handler fed.
  // No component-level message handler needed; all persistence and extraction
  // handled by global handler in ConnectionStateContext.
  useEffect(() => {
    let cleanupFunction = null;

    if (chatRoomId) {
      joinChatRoom(chatRoomId, () => {})
        .then((cleanup) => {
          cleanupFunction = cleanup;
          console.log('[ProfileConnectionPage] Joined chat room successfully');
          setChannelState('connected');
        })
        .catch((error) => {
          console.error('[ProfileConnectionPage] Error joining chat room:', error);
          setChannelState('error');
        });

      // Cleanup function
      return () => {
        if (cleanupFunction) {
          console.log('[ProfileConnectionPage] Cleaning up chat room connection');
          cleanupFunction();
        }
      };
    }
  }, [chatRoomId]);

  // Save notes via context (no direct localStorage access)
  const handleNoteSave = useCallback(async () => {
    console.log('[ProfileConnectionPage] Saving note via context:', note);

    if (chatRoomId) {
      // Use context method - handles both localStorage AND context state
      await updateNotes(chatRoomId, note, true);
      console.log('[ProfileConnectionPage] Note saved successfully:', note);
    }
  }, [chatRoomId, note, updateNotes]);

  // Handle image click - navigate to detail page
  const handleImageClick = useCallback(async () => {
    console.log('[PROFILE_CONNECTION] Image clicked, navigating to detail page');

    // Save notes before navigating away
    await handleNoteSave();

    // Navigate to the detail page, passing chatRoomId explicitly
    navigateTo('profile-connection-detail', { chatRoomId });
  }, [navigateTo, handleNoteSave, chatRoomId]);

  // Handle Plus button click - open share more overlay
  const handleShareContact = useCallback(() => {
    // Composer share entry → the single-action tray (decision #14); the full
    // checked editor stays behind the share sheet's "+" and the Detail page
    setShowShareTray(true);
  }, []);

  // Handle photo capture — optimistic display, background upload, in-place update
  const handleTakePhoto = useCallback(
    async (file) => {
      const senderId = getSenderId();
      const blobUrl = URL.createObjectURL(file);

      // Optimistic message with blob URL
      const message = buildImageChatMessage({
        senderId,
        senderType: 'profile',
        imageUrl: blobUrl,
        uploading: true,
      });

      // Display immediately in chat
      addChatMessage(chatRoomId, message);

      try {
        // Compress and upload
        const publicUrl = await uploadChatImage(file, chatRoomId);

        // Update message in-place: same ID, new content → triggers content-change path
        const updated = { ...message, content: publicUrl, uploading: undefined };
        addChatMessage(chatRoomId, updated);

        // Send to other user via WebSocket
        await sendMessage(chatRoomId, {
          ...updated,
          uploading: undefined,
        });

        // Persist to DB
        await persistMessage(chatRoomId, updated);

        // D3 Moments: the exchange photo ALSO becomes the meeting's first
        // photo-moment (additive — the chat image above still shows on today's
        // screens; the moment shows on the new Detail shelf). Same uploaded URL,
        // always in-window (taken at the exchange).
        if (isNewExperience()) {
          const { ok } = await sendPhotoMoment(chatRoomId, connectionMetadata, publicUrl);
          if (ok) logMomentSent(chatRoomId, 'exchange_chat');
        }

        // Revoke blob URL
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('[ProfileConnectionPage] Photo upload failed:', err);
        // Mark as failed — triggers retry affordance
        const failed = { ...message, uploadFailed: true, uploading: undefined };
        addChatMessage(chatRoomId, failed);
      }
    },
    [chatRoomId, addChatMessage, connectionMetadata]
  );

  // Tap Invite → send the invite immediately (no overlay, no note). The Anon sees the
  // WelcomeInviteBanner's invite variant; the Profile sees the InviteSentNotice land in
  // chat. The header Invite button then hides (inviteSent) — the lasting "already invited"
  // signal.
  const handleInviteButtonClick = useCallback(async () => {
    await sendRoyaleInvite(chatRoomId, (message) =>
      handleSetChatMessages((prev) => [...prev, { ...message }])
    );
    logFunnelInviteSent(chatRoomId);
  }, [chatRoomId, handleSetChatMessages]);

  // ========== UNIFIED SHARE SHEET (fresh-exchange landing) ==========

  const alreadyShared = localStorage.getItem(`contact_shared_${chatRoomId}`) === 'true';

  // In-flight guard (one authoritative write per state change)
  const [shareInFlight, setShareInFlight] = useState(false);

  // The sheet interposes ONLY on a connect landing: every exchange path
  // (qrDetection, qrPXPExchange host + guest) stages it (connectSheets) before
  // navigating here — a repeat connect too. Never consume the staging (QRScreen's
  // channel cleanup reads it, isRoomStaged); the shown-flag owns once-only. One surface,
  // one tap = greet + share (the greet → share wizard collapsed, 2026-07-18 —
  // strictly better under contract #8's one-ask rule); the greeting field is
  // Anon↔Profile only (#9d) — exchangeType read synchronously from the metadata
  // persisted BEFORE navigation (direct-read exception pattern).
  const [sheetInit] = useState(() => {
    const staged = { sheet: false, greeting: false, venueName: '', neighborhood: '' };
    // Which sheets this connect raises (connectSheets): "Where you met" on a first
    // meeting or re-meet, the share sheet on every connect — a repeat included, even
    // though they shared before
    const connect = readStagedSheets(chatRoomId);
    if (!connect) return staged;

    let isPXPRoom = false;
    let stagedVenueName = '';
    let stagedNeighborhood = '';
    try {
      const meta = JSON.parse(localStorage.getItem(`connection_metadata_${chatRoomId}`) || '{}');
      isPXPRoom = meta.exchangeType === 'pxp';
      // D5: the sheet's venue SNAPSHOT — resolved-at-stage-time only, no live
      // Radar update mid-open (rundown 2026-07-27); a late resolve rides
      // today's post-send banner mechanics instead. PXP included (user,
      // /review): the accuracy-glance + edit half applies there too — each
      // side sees and edits their OWN venue (per-user banners), and their
      // own pick confirms it (the venue gate covers PXP).
      stagedVenueName = meta.locationData?.venueInfo?.venue?.name || '';
      stagedNeighborhood = meta.locationData?.venueInfo?.address?.neighborhood || '';
    } catch {
      // no readable metadata → anon_profile default
    }

    const sheetShown = sessionStorage.getItem(shareSheetShownKey(chatRoomId)) === 'true';
    return {
      sheet: !sheetShown && (connect.repeat || !alreadyShared),
      whereYouMet: connect.whereYouMet,
      repeat: connect.repeat,
      greeting: !isPXPRoom,
      isPXPRoom,
      venueName: stagedVenueName,
      neighborhood: stagedNeighborhood,
    };
  });
  // THREAD (plan 3 Steps 9-10, designs profile_1/profile_2) splits the single
  // crowded raise into TWO sequential sheets: "Where you met" first, then
  // "Share". Under every other variant the share sheet raises directly, as it
  // always has.
  const isThread = isThreadVariant();
  // PXP takes the same two-sheet sequence (D18) — it already auto-raises the
  // same sheet on the same page, and venue-inside-share is exactly the crowding
  // the split fixes. PXP carries no variant gate, so this lands under every
  // variant at merge (D20, deliberate). Read from the sheet's own snapshot
  // rather than the `isPXP` derived above: this initialises state on the first
  // render, which can precede context metadata landing.
  const twoSheetSequence = isThread || sheetInit.isPXPRoom;
  // A reconnect skips "Where you met" (same meeting, place already set) and opens
  // straight onto the share sheet
  const [showThreadVenue, setShowThreadVenue] = useState(
    twoSheetSequence && sheetInit.sheet && sheetInit.whereYouMet
  );
  const [showSheet, setShowSheet] = useState(
    sheetInit.sheet && (!twoSheetSequence || !sheetInit.whereYouMet)
  );
  // PXP fresh rooms: pills only, no greeting field (contract #9d). THREAD has
  // no greeting field at all — the mocks show none, and a greeting here is
  // what put an unasked-for "Hi it's {name}" bubble in the Anon's thread.
  const greetingEnabled = sheetInit.greeting && !isThread;

  // The sheet's venue line — the stage-time snapshot, refreshed only by the
  // person's own pencil edit (the picker pick must show; background resolves
  // must not swap the line mid-glance)
  const [sheetVenueName, setSheetVenueName] = useState(sheetInit.venueName);
  // The venue line the share sheet actually SHOWS — the two-sheet sequence
  // (THREAD, PXP) shows none ("Where you met" carries the place there). The
  // render and send-confirm both read this, so only a venue the person saw
  // can be confirmed by sending.
  const shownSheetVenue = twoSheetSequence ? '' : sheetVenueName;

  // D5: Send = confirmation. A venue visible in the sheet at send is confirmed
  // by the send itself — the accuracy glance already happened. Queued as state
  // so the flip runs through handleVenueConfirm (defined with the venue
  // section below) AFTER the share commit — it can never delay the share
  // (Goal 4). Radar-unresolved-at-send keeps today's post-send confirm
  // mechanics exactly: nothing queues, the glyphs appear when a venue resolves.
  const [sheetSendConfirmQueued, setSheetSendConfirmQueued] = useState(false);
  // Backing out of "Other" in "Where you met" and then sharing confirms nothing
  const queueSheetSendConfirm = useCallback(() => {
    if (shownSheetVenue) setSheetSendConfirmQueued(true);
  }, [shownSheetVenue]);

  // Mark staged as soon as it shows — an abandoned sheet (app killed
  // mid-decision) must not re-stage on the next visit. Reuses the wizard-era
  // key, so mid-deploy rooms that already saw the old sheet don't re-see the
  // new one; greeting_shown_ writes stop (orphaned keys are harmless —
  // localstorage-reference § Session Flags retirement precedent).
  useEffect(() => {
    if (showSheet && chatRoomId) {
      sessionStorage.setItem(shareSheetShownKey(chatRoomId), 'true');
    }
  }, [showSheet, chatRoomId]);

  // Greeting prefill (D4+D14): saved greeting > default "Hi it's {firstName}!"
  // (first name models the friendly norm — full-name prefill read business-y).
  // The default is computed fresh each open, so a name change flows through;
  // only EDITED greetings persist (below).
  const defaultGreeting = ownProfileData?.name
    ? `Hi it's ${firstNameOf(ownProfileData.name)}!`
    : '';
  const greetingPrefill = ownProfileData?.greetingText || defaultGreeting;

  // The greeting rides along with the share: it posts first, as the Profile's
  // own regular chat bubble — the field's EXACT text (contract #9b; never
  // touches the name on record). Empty text posts nothing (cleared field =
  // share only, no words in anyone's mouth) — and never persists: clearing is
  // per-send, not a preference, and an explicit '' would clear the saved blob
  // value (the sync merge honors ''). Returns whether it posted, so callers
  // know to stagger the share line.
  //
  // D14: a non-empty EDITED greeting saves on send and prefills every future
  // sheet — nickname freedom ("Hi it's Alex!" alongside a shared
  // "Alexandra"; no linkage to the name field). DB-first: greetingText key in
  // the profiles.profile_data blob (syncProfileToSupabase non-destructive
  // merge + OTP cross-device recovery). The computed default deliberately
  // never persists — a frozen default would outlive a name change.
  const postGreeting = useCallback(
    (text) => {
      if (!text) return false;
      const msg = buildSentChatMessage({
        senderType: 'profile',
        senderId: getSenderId(),
        content: text,
      });
      handleSetChatMessages((prev) => [...prev, msg]);
      sendMessage(chatRoomId, msg);

      if (text !== defaultGreeting && text !== ownProfileData?.greetingText) {
        // Fire-and-forget — the greeting beat never waits on storage; the
        // recipe lives in the data layer (saveGreetingText)
        saveGreetingText(text).then((updated) => {
          if (updated) setOwnProfileData(updated);
        });
      }
      return true;
    },
    [chatRoomId, handleSetChatMessages, defaultGreeting, ownProfileData?.greetingText]
  );

  // One place owns the beat math (decision 28): speak immediately when no
  // greeting posted, else after whatever remains of the beat since the post
  // (measured from the post, so commit latency counts toward the beat)
  const speakAfterGreeting = useCallback((greeted, greetedAt, speak) => {
    if (!greeted) {
      speak();
      return;
    }
    setTimeout(speak, Math.max(0, CHAT_TOKENS.MESSAGE_BEAT_MS - (Date.now() - greetedAt)));
  }, []);

  // Content label for the sheet's black last-share pill — null (single-Phone
  // variant) unless the stored set passes BOTH gates: canQuickLastShare (≥1 field
  // still resolving AND >1 available contact) and the label's own phone-only
  // dedup rule. The gates are distinct — neither subsumes the other.
  const lastShareLabel = useMemo(() => {
    const stored = readLastSharedFields();
    return canQuickLastShare(stored, ownProfileData)
      ? formatLastShareLabel(stored, ownProfileData)
      : null;
  }, [ownProfileData]);

  // ========== ONE-SEND TALK PROMPT (open-exchange ruling, 2026-07-17) ==========
  // After sharing, aim the Profile at the thing that makes the chat worth
  // keeping: the composer reads "What we talked about" until their first sent
  // message, then never returns for this room. Session-backed so it survives
  // the share -> chat moment. (D13's photo-prompt fold was tried and REVERTED
  // at device pass 1, 2026-07-27 — two prompts in a placeholder wrapped and
  // read as clutter; the photo ask needs a better home, filed in the plan.)
  const [talkPromptActive, setTalkPromptActive] = useState(
    () => sessionStorage.getItem(`talk_prompt_${chatRoomId}`) === 'pending'
  );
  const armTalkPrompt = useCallback(() => {
    sessionStorage.setItem(`talk_prompt_${chatRoomId}`, 'pending');
    setTalkPromptActive(true);
  }, [chatRoomId]);
  const clearTalkPrompt = useCallback(() => {
    sessionStorage.removeItem(`talk_prompt_${chatRoomId}`);
    setTalkPromptActive(false);
  }, [chatRoomId]);

  // ========== SPEECH LINES (social-track #13: chat shares speak) ==========

  // Single-channel share → its value line, EVERY room type (device pass 13
  // ruling: a PXP share that lands silently reads as a bug — one grammar
  // everywhere; supersedes #9d's no-PXP-share-lines rule). Identical resends
  // (same type + same value as the last line of that type) are silently
  // dropped, the data commit having been a harmless upsert.
  // THREAD (designs anon_3/anon_7): the Anon sees the host's share as a CONTACT
  // CARD row — "[icon] Bri Larsen ›" — never the raw values. Values live behind
  // the card on the Detail page, which is also the stricter read: the thread
  // shows that a card arrived, not someone's phone number sitting in a bubble.
  //
  // Under CHAT the share posts as speech (a value bubble), which is what put
  // "212-555-1212" in the Anon's thread.
  // The once-only guard lives HERE, not at the call sites: the host has FIVE
  // display paths (sheet pill, Full profile, recall pill, tray, selection
  // editor) and guarding at each is how the first attempt missed
  // postContactLinkLine and shipped "My contact" instead of the card.
  const postShareCard = useCallback(
    (sharedTypes) => {
      const messages = connectionData?.chatMessages || [];
      // Scoped to MY OWN card, not "any card in the room" (device 2026-08-17).
      // The unscoped version was correct for Anon↔Profile — only one side is a
      // Profile there, so the only card that can exist is mine — and silently
      // wrong for PXP, where BOTH sides post one: the peer's card arrives by
      // realtime, and whoever shares second hits this guard and never posts.
      // Observed exactly that: Orson's card at 16:30:12, Mark's
      // profile_data_response at 16:30:13 with no card of his own. The data
      // committed; only the display was swallowed.
      const myCardSenderId = getProfileUserId();
      if (
        messages.some(
          (m) => m.type === 'contact_share' && m.cardStyle && m.senderId === myCardSenderId
        )
      )
        return;
      const card = buildContactShareMessage({
        senderId: getProfileUserId(),
        sender: 'profile',
        cardStyle: true,
        profileName: ownProfileData?.name || '',
        sharedContactTypes: sharedTypes || [],
        profileData: ownProfileData || null,
      });
      handleSetChatMessages((prev) => [...prev, card]);
      sendMessage(chatRoomId, card);
      armTalkPrompt();
    },
    [connectionData?.chatMessages, chatRoomId, handleSetChatMessages, ownProfileData, armTalkPrompt]
  );

  const postShareLine = useCallback(
    (shareType, shareValue) => {
      if (!shareValue) return;
      const messages = connectionData?.chatMessages || [];
      // THREAD: the card replaces the speech line.
      if (isThread) {
        postShareCard([shareType]);
        return;
      }
      const lastOfType = messages.findLast(
        (m) => m.type === 'contact_share' && m.speech && m.shareType === shareType
      );
      if (lastOfType && lastOfType.shareValue === shareValue) return;
      const line = buildShareLineMessage({
        senderId: getProfileUserId(),
        shareType,
        shareValue,
      });
      handleSetChatMessages((prev) => [...prev, line]);
      sendMessage(chatRoomId, line);
      armTalkPrompt();
    },
    [
      connectionData?.chatMessages,
      chatRoomId,
      handleSetChatMessages,
      armTalkPrompt,
      isThread,
      postShareCard,
    ]
  );

  // Multi-select share → "My contact" (linked to detail), every room type.
  // Every completed multi-select posts a line — a deliberate Update is a share
  // act (decision #13; single-line dedup doesn't apply to re-affirmations).
  const postContactLinkLine = useCallback(
    (sharedTypes) => {
      // THREAD: the CARD is the multi-select's display too. This path is the
      // one the first attempt missed — it bypasses postShareLine entirely, so
      // "My contact" reached the thread instead of the designed card.
      if (isThread) {
        postShareCard(sharedTypes);
        return;
      }
      const line = buildContactLinkMessage({ senderId: getProfileUserId() });
      handleSetChatMessages((prev) => [...prev, line]);
      sendMessage(chatRoomId, line);
      armTalkPrompt();
    },
    [chatRoomId, handleSetChatMessages, armTalkPrompt, isThread, postShareCard]
  );

  // One-tap quick shares — straight through commitSharedFields (data only now;
  // callers own display per #13). updateGlobalDefaults:false — a quick tap is a
  // shortcut, not a preference declaration (user-decided 2026-07-08).
  const handleQuickShare = useCallback(
    async (sharedFields, isFullName, shareMethod) => {
      if (shareInFlight || !ownProfileData) return null;
      setShareInFlight(true);
      try {
        return await commitSharedFields({
          chatRoomId,
          sharedFields,
          isFullName,
          profileData: ownProfileData,
          connectionData,
          updateContactSharingStatus,
          updateGlobalDefaults: false,
          shareMethod,
        });
      } finally {
        setShareInFlight(false);
      }
    },
    [shareInFlight, ownProfileData, chatRoomId, connectionData, updateContactSharingStatus]
  );

  // The sheet's filled channels, lead order from the registry — first two
  // collapsed, the rest behind More
  const sheetTypeIds = useMemo(() => {
    if (!ownProfileData) return [];
    const filled = CONTACT_FIELDS.filter((t) => ownProfileData[t]);
    return [
      ...SHEET_SHARE_ORDER.filter((t) => filled.includes(t)),
      ...filled.filter((t) => !SHEET_SHARE_ORDER.includes(t)),
    ];
  }, [ownProfileData]);

  // "Full profile" collapses to the single channel pill when it would send
  // NOTHING that pill doesn't (user ruling 2026-08-17, device — a name-and-
  // phone profile was showing [Full profile] and [Phone] as two buttons for
  // one payload).
  //
  // The delta really is only the channel set: handleSheetTypeShare sends
  // ['photoUrl', typeId, ...currentCity] and handleSheetFullProfile sends
  // ['photoUrl', ...channelKeys, ...currentCity] — photo and city are in BOTH,
  // and the photo was already auto-shared at formation anyway (qrDetection
  // sends ['name','photoUrl'] on the CHAT/THREAD path). So "is Full profile
  // redundant?" reduces to "does it carry more than one channel?"
  //
  // Compared against getAvailableSharingFields, NOT sheetTypeIds: that list is
  // broader (custom links, youtube, the *2 secondaries), so a host with a phone
  // AND a custom link has one contact PILL but a two-channel Full profile —
  // collapsing there would silently drop the custom link from the one-tap. The
  // equality check keeps it conservative: collapse only when row one's pill
  // provably IS the whole of Full profile.
  //
  // No label change is needed and none is wanted: with onFullProfile null and
  // no recall pill (canQuickLastShare already requires >1 available contact),
  // ShareSheet's existing primaryTypeId rule renders that pill black in row
  // one. "Phone" is also the honester label — calling a name and a number a
  // "full profile" overstates what the other person is about to receive.
  const fullProfileChannels = useMemo(
    () => getAvailableSharingFields(ownProfileData).map((f) => f.key),
    [ownProfileData]
  );
  const fullProfileIsRedundant = isFullProfileRedundant(fullProfileChannels, sheetTypeIds[0]);

  // Decision #13's display rule, ONE home for every commit path: multi-select
  // speaks as the contact link; a single type speaks as its value line
  const speakShare = useCallback(
    (types) => {
      if (!types || types.length === 0) return;
      if (types.length > 1) postContactLinkLine(types);
      else postShareLine(types[0], ownProfileData?.[types[0]]);
    },
    [postContactLinkLine, postShareLine, ownProfileData]
  );

  // Sheet taps close the sheet immediately — the greeting bubble posts right
  // away, then the speech line lands in the revealed room when the commit
  // resolves (its pop-on-land is the confirmation). Two separate messages,
  // greeting first (decision 2).
  const handleSheetLastShare = useCallback(
    async (greetingText) => {
      setShowSheet(false);
      const greeted = postGreeting(greetingText);
      const greetedAt = Date.now();
      const stored = readLastSharedFields();
      if (!stored) return;
      const result = await handleQuickShare(
        stored,
        readGlobalNameSharing() === 'full',
        'last_share'
      );
      if (!result) return;
      queueSheetSendConfirm();
      speakAfterGreeting(greeted, greetedAt, () => speakShare(result.sharedContactTypes));
    },
    [postGreeting, speakAfterGreeting, handleQuickShare, speakShare, queueSheetSendConfirm]
  );

  // One-tap single-channel share: same data recipe as the shipped Phone pill
  // (photo + the channel + hometown-when-present + full name — the labeled pill
  // is the confirmation act); display = that channel's speech line only.
  const handleSheetTypeShare = useCallback(
    async (typeId, greetingText) => {
      setShowSheet(false);
      const greeted = postGreeting(greetingText);
      const greetedAt = Date.now();
      const fields = ['photoUrl', typeId, ...(ownProfileData?.currentCity ? ['currentCity'] : [])];
      const result = await handleQuickShare(
        fields,
        true,
        typeId === 'phone' ? 'phone' : `sheet_${typeId}`
      );
      if (!result) return;
      queueSheetSendConfirm();
      speakAfterGreeting(greeted, greetedAt, () => postShareLine(typeId, ownProfileData?.[typeId]));
    },
    [
      postGreeting,
      speakAfterGreeting,
      handleQuickShare,
      postShareLine,
      ownProfileData,
      queueSheetSendConfirm,
    ]
  );

  // The + circle: full share editor (saved-preference init; shareMethod 'edit').
  // The greeting holds until editor COMPLETION — backing out posts nothing
  // (decision 3). The sheet is shown-once, so the composer share entry keeps
  // sharing one tap away.
  const pendingGreetingRef = useRef('');
  // THREAD: the venue answer hands off to the share sheet. One-way — the
  // venue sheet never comes back in this room (the share sheet's own
  // shown-flag still owns once-only semantics for the pair).
  const advanceThreadToShare = useCallback(() => {
    setShowThreadVenue(false);
    setShowSheet(true);
  }, []);

  // THREAD's black primary (Step 10, D4): the host's ONE deliberate tap sends
  // the full card — every filled channel plus photo and city. This is why
  // THREAD needs no auto-broadcast at formation and can keep the stricter
  // first-name+photo payload: richness arrives because the person chose it.
  const handleSheetFullProfile = useCallback(
    async (greetingText) => {
      setShowSheet(false);
      const greeted = postGreeting(greetingText);
      const greetedAt = Date.now();
      const channelKeys = fullProfileChannels;
      // "Full profile" means FULL: every channel, the photo, the city AND the
      // About-me bundle (user ruling, 2026-08-20) — one home for the list,
      // sharingFields.fullProfileFields.
      //
      // Deliberately NOT added to the single-channel pills below: a pill
      // labelled "Phone" promises a number, and photo/name/city are the
      // identity floor. If every pill carried About-me too, the Full profile
      // button would differ from a grey pill by one channel.
      const fields = fullProfileFields(ownProfileData);
      const result = await handleQuickShare(fields, true, 'sheet_full_profile');
      if (!result) return;
      queueSheetSendConfirm();
      // The card carries EVERY channel it stands for, not just the first —
      // "Full profile" is the whole card by definition.
      speakAfterGreeting(greeted, greetedAt, () => postShareCard(channelKeys));
    },
    [
      postGreeting,
      speakAfterGreeting,
      handleQuickShare,
      postShareCard,
      ownProfileData,
      fullProfileChannels,
      queueSheetSendConfirm,
    ]
  );

  const handleSheetMore = useCallback((greetingText) => {
    pendingGreetingRef.current = greetingText || '';
    setShowSheet(false);
    setShowShareMoreOverlay(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    setShowSheet(false);
  }, []);

  // Share-editor completion (the tray has its own value-known path): the held
  // greeting posts first, then the "My contact" line a beat later
  const handleShareMoreComplete = useCallback(
    (_roomId, data) => {
      setShowShareMoreOverlay(false);
      const greeted = postGreeting(pendingGreetingRef.current);
      pendingGreetingRef.current = '';
      // Third send path (D5): the editor completes a share staged from the
      // sheet — the sheet's venue glance still counts
      queueSheetSendConfirm();
      speakAfterGreeting(greeted, Date.now(), () => speakShare(data?.sharedContactTypes));
    },
    [postGreeting, speakAfterGreeting, speakShare, queueSheetSendConfirm]
  );

  // signup-connect step 4: once a fresh profile-to-profile exchange's sheets
  // are all closed — any exit, the share settled — land on the person's own
  // People, for every pair (Mark, 2026-10-05). Two Full people's conversation
  // still exists; only the landing moves. Other rooms keep this page. A repeat
  // connect stays in the conversation, as it always has — its People landing (card
  // on top, `Already connected`) ships with the new screens (E3, Mark 2026-10-16).
  const landedOnPeopleRef = useRef(false);
  useEffect(() => {
    if (!sheetInit.isPXPRoom || !sheetInit.sheet || sheetInit.repeat) return;
    if (landedOnPeopleRef.current) return;
    if (showThreadVenue || showSheet || showShareMoreOverlay || showLocationOverlay) return;
    if (shareInFlight) return;
    landedOnPeopleRef.current = true;
    navigateTo('profile-connections-list', {});
  }, [
    sheetInit.isPXPRoom,
    sheetInit.sheet,
    sheetInit.repeat,
    showThreadVenue,
    showSheet,
    showShareMoreOverlay,
    showLocationOverlay,
    shareInFlight,
    navigateTo,
  ]);

  // ========== COMPOSER SHARE TRAY (decision #14) ==========

  // One row per FILLED contact value not yet shared with this connection
  // (secondary variants are their own rows; already-shared values drop —
  // updated values sync silently via the Detail page). Recomputed on open so
  // a just-completed share falls out of the list.
  const trayRows = useMemo(() => {
    if (!ownProfileData || !showShareTray) return [];
    const sent = getSentSharedFields(chatRoomId);
    // The registry owns "filled" (incl. the junk-phone digit gate and secondary
    // variants); the tray just filters the ledger and sorts by the lead order.
    // 'custom' is a labeled link, not a bare value — it stays editor-only.
    const sheetRank = (f) => {
      const i = SHEET_SHARE_ORDER.indexOf(getBaseType(f.key));
      return i === -1 ? SHEET_SHARE_ORDER.length : i;
    };
    return getAvailableSharingFields(ownProfileData)
      .filter((f) => f.key !== 'custom' && !sent.includes(f.key))
      .sort((a, b) => sheetRank(a) - sheetRank(b))
      .map((f) => ({ fieldKey: f.key, value: ownProfileData[f.key] }));
  }, [ownProfileData, showShareTray, chatRoomId]);

  // Tray tap = one ADDITIVE share (decision #15: existing ∪ new — a one-thing
  // share never shrinks the ledger) → that value's speech line
  const handleTrayShare = useCallback(
    async (fieldKey, value) => {
      setShowShareTray(false);
      const prior = getSentSharedFields(chatRoomId);
      const fields = [...new Set([...prior, 'photoUrl', fieldKey])];
      const isFullName =
        (localStorage.getItem(`name_sharing_${chatRoomId}`) || readGlobalNameSharing()) === 'full';
      const result = await handleQuickShare(fields, isFullName, 'tray');
      if (result) postShareLine(getBaseType(fieldKey), value);
    },
    [chatRoomId, handleQuickShare, postShareLine]
  );

  // GROUP CARD RETIRED (variants block plan 3, D19 — reverses the 2026-07-17
  // PXP-only ruling). The connection moment no longer prompts for structure on
  // ANY variant: Anon rooms have always kept the composer clear because the
  // chat is the artifact, and PXP now converges with them. Group assignment
  // stays fully available on the Detail page — the trigger is gone, not the
  // capability, and `GroupSuggestCard` stays in the codebase for a later home.
  // The full group picker went with it (the card's + was its only door here).

  // ========== HOST PHOTO CHIP (THREAD, profile_3 — Step 11) ==========
  // The last beat of the host's sequence: right after their card posts, the
  // natural next move is a photo of the person in front of them, and it lands
  // in the thread as a shared image both sides keep.
  //
  // Waits for their own share (the sequence is ordered, and a photo prompt
  // over an unfinished exchange is a second ask), holds while the share sheet
  // is interposed (one ask at a time — the retired group card's contract #8,
  // which outlives it), and never nags twice: answered EITHER way retires it
  // for good. localStorage, matching the dismissal grammar the group card
  // used, so it survives a reload of a room they already answered.
  const photoChipKey = `host_photo_chip_done_${chatRoomId}`;
  const [photoChipDone, setPhotoChipDone] = useState(
    () => localStorage.getItem(photoChipKey) === 'true'
  );
  const retireHostPhotoChip = useCallback(() => {
    localStorage.setItem(photoChipKey, 'true');
    setPhotoChipDone(true);
  }, [photoChipKey]);
  const showHostPhotoChip =
    isThread &&
    alreadyShared &&
    !showSheet &&
    !showThreadVenue &&
    !inactiveRoom &&
    dotStatus !== 'deleted' &&
    !photoChipDone;
  const hostPhotoChip = (
    <HostPhotoChip
      onTakePhoto={(file) => {
        retireHostPhotoChip();
        handleTakePhoto(file);
      }}
      onTap={() => logMomentEntryTapped(chatRoomId, 'exchange_chat')}
      onSkip={retireHostPhotoChip}
    />
  );

  // ========== VENUE CONFIRM AFFORDANCES (contract #7/#8) ==========
  // Unlock on own share: the banner shows the ungated suggested venue underlined
  // (banner tap → LocationOverlay is the edit) with a floating check circle.
  // Check = confirm-and-share; edit-select confirms via the existing onVenueChange
  // path. No suggested venue → nothing to confirm, no affordances (the banner's
  // "Add a place" flow stays). Anon↔Profile only — a PXP person picks by
  // tapping their own banner (LocationOverlay) or in "Where you met".
  const suggestedVenueName = contextConnectionMetadata?.locationData?.venueInfo?.venue?.name || '';
  const showVenueConfirm =
    !isPXP &&
    alreadyShared &&
    !!suggestedVenueName &&
    contextConnectionMetadata?.venueConfirmed !== true;

  // The venue-change tail, ONE home (refactor log 2026-07-15, absorbed here):
  // re-render + persist the owner's banner from the updated metadata, log the
  // manual confirmation (human confirmation is the accuracy signal), and sync
  // the Anon via metadata_updated. Banner lookup prefers the per-user id (PXP)
  // and falls back to the shared old-format id (Anon↔Profile). PXP: venue is
  // per-user — banner still updates, nothing is broadcast.
  const syncBannerAndPeer = useCallback(
    async (updatedMetadata) => {
      const userId = getProfileUserId();
      const currentMessages = contextChatMessages || [];
      const newContent = formatSystemMessageLocation(updatedMetadata);
      if (newContent) {
        const ownSystemMessages = currentMessages.filter(
          (msg) => msg.type === 'system' && msg.id?.endsWith(`_${userId}`)
        );
        const target =
          ownSystemMessages.length > 0
            ? ownSystemMessages[ownSystemMessages.length - 1]
            : currentMessages.find(
                (msg) => msg.type === 'system' && msg.id === `system_${chatRoomId}`
              );
        if (target && newContent !== target.content) {
          updateChatMessages(
            chatRoomId,
            currentMessages.map((m) => (m.id === target.id ? { ...m, content: newContent } : m))
          );
          persistMessage(chatRoomId, { ...target, content: newContent });
        }
      }

      logConnectionLocation(chatRoomId, updatedMetadata.locationData, 'manual');

      if (!isPXP) {
        try {
          await sendMessage(
            chatRoomId,
            buildMetadataUpdatedMessage({
              connectionMetadata: updatedMetadata,
              senderProfileUserId: getProfileUserId(),
            })
          );
        } catch (error) {
          console.error('[ProfileConnectionPage] Error sending metadata_updated:', error);
        }
      }
    },
    [contextChatMessages, chatRoomId, updateChatMessages, isPXP]
  );

  // Check tap: flip venueConfirmed, then the shared tail (banner re-persist now
  // passes the display gate — the Anon's banner upgrade falls out of the
  // formatter gate on their side).
  // The venue write, ONE home: the picker's pick and THREAD's neighborhood
  // row both land here. Passing null means "no specific venue" — the location
  // string falls to the neighborhood and the banner reads "Met in {area}".
  const handleVenueChange = useCallback(
    async (selectedVenue) => {
      const address = contextConnectionMetadata?.locationData?.venueInfo?.address || {};
      const coords = contextConnectionMetadata?.locationData?.coords || {};

      // Save custom venue for future lookups at this location
      if (selectedVenue?.isCustom && selectedVenue?.name && coords.latitude && coords.longitude) {
        saveCustomVenue(selectedVenue.name, coords.latitude, coords.longitude);
      }

      const updatedMetadata = {
        ...contextConnectionMetadata,
        venueConfirmed: true,
        locationData: {
          ...contextConnectionMetadata?.locationData,
          venueInfo: {
            ...contextConnectionMetadata?.locationData?.venueInfo,
            venue: selectedVenue,
          },
        },
      };

      // Build location string
      let locationString = '';
      if (selectedVenue?.name) {
        const parts = [];
        if (address.streetNumber && address.street) {
          parts.push(`${address.streetNumber} ${address.street}`);
        } else if (address.street) {
          parts.push(address.street);
        }
        if (address.cityAbbr || address.city) {
          parts.push(address.cityAbbr || address.city);
        }
        locationString = `${selectedVenue.name}${parts.length > 0 ? ', ' + parts.join(', ') : ''}`;
      } else {
        const parts = [];
        if (address.streetNumber && address.street) {
          parts.push(`${address.streetNumber} ${address.street}`);
        } else if (address.street) {
          parts.push(address.street);
        }
        if (address.neighborhood) parts.push(address.neighborhood);
        if (address.cityAbbr || address.city) {
          parts.push(address.cityAbbr || address.city);
        }
        locationString = parts.join(', ');
      }
      updatedMetadata.location = locationString;

      // A pencil-edit pick refreshes the sheet's snapshot (the person's
      // own choice must show; background resolves never do). The pick
      // itself already confirmed — send-confirm no-ops after this.
      setSheetVenueName(selectedVenue?.name || '');

      // Update context + sync meeting row in DB, then the shared tail
      // (banner re-persist, manual log, peer sync — syncBannerAndPeer)
      await updateConnectionMetadata(chatRoomId, updatedMetadata);
      setMeetingVenue(
        chatRoomId,
        getProfileUserId(),
        updatedMetadata.meetingKey,
        updatedMetadata.locationData.venueInfo
      );
      await syncBannerAndPeer(updatedMetadata);

      setShowLocationOverlay(false);
    },
    [contextConnectionMetadata, chatRoomId, updateConnectionMetadata, syncBannerAndPeer]
  );

  const handleVenueConfirm = useCallback(async () => {
    if (!contextConnectionMetadata) return;
    const updated = {
      ...contextConnectionMetadata,
      venueConfirmed: true,
      lastUpdated: new Date().toISOString(),
    };
    await updateConnectionMetadata(chatRoomId, updated);
    // The meeting row was seeded without the venue (gated at the write) —
    // this confirmation is what puts it there (same rail as handleVenueChange)
    const venueInfo = updated.locationData?.venueInfo;
    if (venueInfo) setMeetingVenue(chatRoomId, getProfileUserId(), updated.meetingKey, venueInfo);
    await syncBannerAndPeer(updated);
  }, [contextConnectionMetadata, chatRoomId, updateConnectionMetadata, syncBannerAndPeer]);

  // D5's queued send-confirm lands here: same flip + shared tail as the check
  // tap, running after the share commit resolved (never blocking the send).
  // The check/pencil glyphs retire with it — showVenueConfirm reads the flag.
  useEffect(() => {
    if (!sheetSendConfirmQueued || !contextConnectionMetadata) return;
    setSheetSendConfirmQueued(false);
    if (contextConnectionMetadata.venueConfirmed === true) return;
    handleVenueConfirm();
  }, [sheetSendConfirmQueued, contextConnectionMetadata, handleVenueConfirm]);

  // Loading state is now handled by ConnectionPageShared

  // IMPORTANT: This passes data to the shared component for PROFILE USER VIEW
  return (
    <>
      {/* Unified share sheet — greeting field + one-tap pills, one surface;
          the prefill arrives async with own profile (touched guard syncs it).
          Prefill = saved greeting > "Hi it's {firstName}!" (D4+D14) */}
      {/* THREAD's FIRST sheet (Step 9, profile_1): "Where you met". Venue used
          to be a pill INSIDE the share sheet, which is what made that surface
          crowded. Splitting it also promotes the neighborhood from a hidden
          Skip to a visible equal option. Writes ride confirmVenue/changeVenue
          and LocationOverlay unchanged. */}
      {showThreadVenue && (
        <HostThreadVenueSheet
          venueName={sheetInit.venueName}
          neighborhood={sheetInit.neighborhood}
          onPickVenue={() => {
            handleVenueConfirm();
            advanceThreadToShare();
          }}
          // Picking the neighborhood is still an ANSWER, not a skip: clear the
          // venue (so the sentence falls to "Met in {neighborhood}") and
          // confirm, which is what posts the banner in the Anon's thread.
          // Without this the neighborhood row was silent and their thread
          // stayed empty.
          onPickNeighborhood={() => {
            handleVenueChange(null);
            advanceThreadToShare();
          }}
          onFreeEntry={() => {
            advanceThreadToShare();
            setShowLocationOverlay(true);
          }}
        />
      )}

      <ShareSheet
        show={showSheet}
        greetingEnabled={greetingEnabled}
        greetingPrefill={greetingPrefill}
        lastShareLabel={lastShareLabel}
        onLastShare={handleSheetLastShare}
        typeIds={sheetTypeIds}
        onTypeShare={handleSheetTypeShare}
        onMore={handleSheetMore}
        onClose={handleSheetClose}
        disabled={shareInFlight}
        // D5: stage-time venue snapshot, every room type (PXP included at
        // /review — the glance+edit half applies; own venue, own record);
        // tap opens the picker over the sheet. Composed by formatMetSentence
        // (one home with the chat banner this line becomes).
        // THREAD moved venue to its own preceding sheet (Step 9), so the
        // share sheet carries no venue pill.
        venueLine={
          shownSheetVenue ? formatMetSentence(shownSheetVenue, contextConnectionMetadata) : null
        }
        onVenueEdit={twoSheetSequence ? undefined : () => setShowLocationOverlay(true)}
        // Step 10 (profile_2): "Full profile" is the black primary and the
        // recall pill drops to row two — THREAD and PXP alike (D18).
        // Suppressed when it would duplicate the one channel pill (D27): the
        // sheet then falls through to that pill as its own black primary.
        onFullProfile={twoSheetSequence && !fullProfileIsRedundant ? handleSheetFullProfile : null}
      />

      {/* Composer share tray — single-action value rows (decision #14) */}
      <ShareTray
        show={showShareTray}
        rows={trayRows}
        onShareValue={handleTrayShare}
        onClose={() => setShowShareTray(false)}
      />

      {/* Share More Overlay — full checked editor (share sheet's + only; the composer Plus opens the tray) */}
      {showShareMoreOverlay && chatRoomId && (
        <SharingSelectionOverlay
          chatRoomId={chatRoomId}
          // X / backdrop = back a step to the sheet for a single-tap share
          // (device pass 10); completing still closes everything. Backing out
          // drops the held greeting — nothing posts (decision 3); the sheet's
          // field remounts to the pre-fill.
          onClose={() => {
            pendingGreetingRef.current = '';
            setShowShareMoreOverlay(false);
            setShowSheet(true);
          }}
          onComplete={handleShareMoreComplete}
          isInitialShare={false}
          selectionMode="multi"
          buttonText={alreadyShared ? 'Update' : 'Share'}
          shareMethod="edit"
        />
      )}

      {/* Wrapper div for consistent overlay darkening */}
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          backgroundColor: 'white',
        }}
      >
        <ConnectionPageShared
          // PROFILE USER VIEW: According to data architecture, receivedAnonData is passed as profileData
          profileData={anonData}
          // The Anon's name bubble becomes their contact card once they have
          // shared a contact (user design 2026-08-20) — the host's side of the
          // same morph, so both sides of the thread end as one card each.
          // Mirrors the Anon page's own predicate against this side's copy of
          // the record; null = nothing shared yet, so the bubble stays speech.
          anonContactCardName={
            (anonData?.sharedFields?.length || 0) > 0 ? anonData?.name || '' : null
          }
          // Navigate back to connections list
          onGoBack={() => {
            goBack('profile-chat-list');
          }}
          // Handle image click to navigate to detail page
          onImageClick={handleImageClick}
          // When false, allows content to render
          isLoading={isLoading}
          // Don't show Add to Contacts button on ProfileConnectionPage (will be on detail page)
          showAddToContacts={false}
          // Profile name editing
          profileName={
            getConnectionDisplayName({ displayName: connectionData?.displayName, anonData }) || ''
          }
          isAnonymousUser={false}
          // Debug props
          chatRoomId={chatRoomId}
          channelState={channelState}
          userType="profile"
          // Persistent chat messages from context
          actualChatMessages={connectionData?.chatMessages || []}
          actualSetChatMessages={handleSetChatMessages}
          // Share contact is now handled on ProfileConnectionDetailPage
          // Connection status for deleted banner logic
          connectionStatus={dotStatus}
          // Activity status for header subtitle (includes anon inactivity labels)
          activityStatus={headerActivityStatus}
          locationDisplay={peerLocation}
          // Open LocationOverlay directly from system message tap
          onSystemMessageClick={() => setShowLocationOverlay(true)}
          // Sentence banner in/at derivation (D6)
          connectionMetadata={contextConnectionMetadata}
          // Venue confirm affordances — underlined suggested venue + check circle
          venueConfirmAffordance={
            showVenueConfirm
              ? { venueName: suggestedVenueName, onConfirm: handleVenueConfirm }
              : null
          }
          // Share contact handler (Plus button in chat input) — always available,
          // messages persist to DB and peer recovers on reconnect
          onShareContact={handleShareContact}
          // Camera — visible after sharing contacts (or always for PXP);
          // quiet in dead rooms (inactiveRoom — see the dead-room rule at
          // the derivation)
          onTakePhoto={!inactiveRoom && (isPXP || alreadyShared) ? handleTakePhoto : undefined}
          // Inactive Anon banner — above chat input
          inactiveBanner={inactiveBanner}
          inactiveStatus={inactiveStatus}
          // Floating slot above the composer. THREAD closes the host's
          // sequence here with the photo chip (profile_3, Step 11): venue
          // sheet → share sheet → their card posts → "take a photo of the
          // person you just met" → composer. It waits for their own share,
          // never nags twice, and leaves for good once answered either way.
          sharePillElement={showHostPhotoChip ? hostPhotoChip : null}
          // Composer Plus — the share re-entry. Hidden while the sheet
          // interposes; prominent (black) until a contact is shared, then gray.
          showPlusButton={!showSheet && !inactiveRoom}
          plusButtonProminent={!alreadyShared}
          // One-send talk prompt after a share; standard otherwise (and never
          // in a dead room)
          // THREAD (profile_3): a clean, unlabelled composer. The talk-prompt
          // placeholder is CHAT's nudge and appears in none of the mocks.
          chatPlaceholder={
            isThread ? '' : talkPromptActive && !inactiveRoom ? 'What we talked about' : 'Message'
          }
          onChatMessageSent={clearTalkPrompt}
          // Invite to Royale props — stays in dead rooms (the door back in)
          showInviteButton={showInviteButton}
          onInviteToRoyale={handleInviteButtonClick}
          inviteSent={inviteSent}
        />
      </div>

      {/* Skip toast removed — no longer needed without sharing gate */}

      {/* Location overlay — opens when system message tapped */}
      {showLocationOverlay && contextConnectionMetadata && (
        <LocationOverlay
          nearbyVenues={contextConnectionMetadata?.locationData?.venueInfo?.nearbyVenues || []}
          currentVenue={contextConnectionMetadata?.locationData?.venueInfo?.venue || null}
          address={contextConnectionMetadata?.locationData?.venueInfo?.address || {}}
          coords={contextConnectionMetadata?.locationData?.coords || {}}
          showLocationContext={false}
          onVenueChange={handleVenueChange}
          onClose={() => setShowLocationOverlay(false)}
        />
      )}
    </>
  );
};

export default ProfileConnectionPage;
